/**
 * Dashboard Table Section Component
 * 
 * Renders a table section from dashboard API response.
 * Generic and reusable - no doctype-specific logic.
 */

import { cn } from "@/lib/utils";
import type { DashboardSection } from "@/lib/erp/docs";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { useMemo, useState } from "react";
import { StockActionsModal } from "./StockActionsModal";

interface DashboardTableSectionProps {
  section: DashboardSection;
  itemCode?: string;
  warehouseCount?: number;
}

export function DashboardTableSection({ section, itemCode, warehouseCount = 0 }: DashboardTableSectionProps) {
  const { language } = useLanguage();
  const [isStockActionsModalOpen, setIsStockActionsModalOpen] = useState(false);
  
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      noDataAvailable: t("no_data_available", "No data available"),
      noWarehousesTitle: t("no_warehouses_title", "No stock locations yet"),
      noWarehousesMessage: t("no_warehouses_message", "Add stock locations through the Inventory tab in the experience view to track stock levels."),
      addStockLocations: t("add_stock_locations", "Add Stock Locations"),
    };
  }, [language]);

  const { label, columns = [], rows = [], meta, error } = section;
  
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }
  
  if (!columns || columns.length === 0) {
    return null;
  }
  
  if (!rows || rows.length === 0) {
    // Show helpful empty state for stock levels when no warehouses
    const isStockLevels = section.id === 'stock_levels';
    const showHelpfulEmptyState = isStockLevels && warehouseCount === 0 && itemCode;
    
    return (
      <>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
          {label && (
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-foreground">{label}</h3>
            </div>
          )}
          <div className="p-8 sm:p-10">
            {showHelpfulEmptyState ? (
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="text-sm font-medium text-foreground">
                  {ui.noWarehousesTitle}
                </div>
                <div className="text-xs text-muted-foreground max-w-md">
                  {ui.noWarehousesMessage}
                </div>
                {itemCode && (
                  <button
                    onClick={() => setIsStockActionsModalOpen(true)}
                    className="mt-2 px-4 py-2 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-sm transition-colors"
                  >
                    {ui.addStockLocations}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center">{ui.noDataAvailable}</p>
            )}
          </div>
        </div>
        {itemCode && (
          <StockActionsModal
            open={isStockActionsModalOpen}
            onOpenChange={setIsStockActionsModalOpen}
            itemCode={itemCode}
            lockWarehouses={false}
          />
        )}
      </>
    );
  }
  
  const primaryKey = meta?.primary_key;
  
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      {label && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/50">
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {rows.map((row, index) => {
              const rowKey = primaryKey && row[primaryKey] 
                ? String(row[primaryKey]) 
                : `row-${index}`;
              
              return (
                <tr
                  key={rowKey}
                  className="hover:bg-gray-50 dark:hover:bg-gray-950/50 transition-colors"
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    const displayValue = value !== null && value !== undefined 
                      ? String(value) 
                      : '-';
                    
                    // Format numbers with locale
                    const isNumeric = typeof value === 'number';
                    const formattedValue = isNumeric 
                      ? value.toLocaleString('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })
                      : displayValue;
                    
                    return (
                      <td
                        key={column.id}
                        className={cn(
                          "px-6 py-4 text-sm",
                          isNumeric ? "text-right font-medium" : "text-left"
                        )}
                      >
                        {formattedValue}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
